import { supabase } from '@/integrations/supabase/client';
import { checkUserIsActive, getRedirectUrl } from './authHelpers';
import type { AuthResult, OAuthProvider } from './authTypes';

export const authService = {
  async signUp(email: string, password: string, fullName: string): Promise<AuthResult> {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: getRedirectUrl(),
          data: { full_name: fullName },
        },
      });

      if (error) {
        return { success: false, error, data: null };
      }

      return {
        success: true,
        error: null,
        data: data.user && data.session 
          ? { user: data.user, session: data.session } 
          : null,
      };
    } catch (error) {
      console.error('SignUp error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Unknown signup error'), 
        data: null 
      };
    }
  },

  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error, data: null };
      }

      // Check if user account is active
      if (data.user) {
        const isActive = await checkUserIsActive(data.user.id);
        if (!isActive) {
          await supabase.auth.signOut();
          return {
            success: false,
            error: new Error('ACCOUNT_DEACTIVATED'),
            data: null,
          };
        }
      }

      return {
        success: true,
        error: null,
        data: { user: data.user, session: data.session },
      };
    } catch (error) {
      console.error('SignIn error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Unknown signin error'), 
        data: null 
      };
    }
  },

  async signInWithOAuth(provider: OAuthProvider): Promise<AuthResult> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: getRedirectUrl() },
      });

      return { success: !error, error, data: null };
    } catch (error) {
      console.error('OAuth error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Unknown OAuth error'), 
        data: null 
      };
    }
  },

  async signOut(): Promise<void> {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('SignOut error:', error);
    }
  },

  async resetPassword(email: string): Promise<AuthResult> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getRedirectUrl('/auth?mode=reset'),
      });

      return { success: !error, error, data: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Unknown reset error'), 
        data: null 
      };
    }
  },

  async getSession() {
    return supabase.auth.getSession();
  },

  onAuthStateChange(callback: Parameters<typeof supabase.auth.onAuthStateChange>[0]) {
    return supabase.auth.onAuthStateChange(callback);
  },
};
